const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

// --- Retry with exponential backoff ---

async function fetchWithRetry(
  input: RequestInfo,
  init?: RequestInit,
  attempts = 3,
): Promise<Response> {
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await fetch(input, init);
      return res;
    } catch (err) {
      // Only retry on network errors (TypeError from fetch), not HTTP errors
      if (i === attempts - 1) throw err;
      await delay(2 ** i * 500);
    }
  }
  // Unreachable, but satisfies TS
  throw new Error('fetchWithRetry: exhausted attempts');
}

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// --- Core request helpers ---

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetchWithRetry(`${API_BASE}${path}`, init);

  if (res.status === 401) {
    setAccessToken(null);
    window.location.href = '/admin/login';
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body?.error?.message ?? res.statusText, body);
  }

  return res.json() as Promise<T>;
}

async function requestBlob(path: string, init?: RequestInit): Promise<Blob> {
  const res = await fetchWithRetry(`${API_BASE}${path}`, init);

  if (res.status === 401) {
    setAccessToken(null);
    window.location.href = '/admin/login';
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    throw new ApiError(res.status, res.statusText);
  }

  return res.blob();
}

function authHeaders(): HeadersInit {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

function adminRequest<T>(path: string, init?: RequestInit): Promise<T> {
  return request<T>(path, {
    ...init,
    headers: { ...authHeaders(), ...init?.headers },
  });
}

function adminJson<T>(
  path: string,
  method: string,
  body?: unknown,
): Promise<T> {
  return adminRequest<T>(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

// --- Error class ---

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ============================================================
// Public API
// ============================================================

export function listPackages(params?: {
  search?: string;
  theme?: string;
}): Promise<unknown> {
  const qs = new URLSearchParams();
  if (params?.search) qs.set('search', params.search);
  if (params?.theme) qs.set('theme', params.theme);
  const query = qs.toString();
  return request(`/packages${query ? `?${query}` : ''}`);
}

export function getPackage(id: string): Promise<unknown> {
  return request(`/packages/${id}`);
}

export function getRecipe(
  packageId: string,
  courseId: string,
): Promise<unknown> {
  return request(`/packages/${packageId}/recipes/${courseId}`);
}

export function getShoppingList(packageId: string): Promise<unknown> {
  return request(`/packages/${packageId}/shopping-list`);
}

export function getPackagePdf(packageId: string): Promise<Blob> {
  // PDF endpoint now returns a JSON response with a presigned S3 URL
  return request<{ url: string }>(`/packages/${packageId}/pdf`).then(
    (data) =>
      fetch(data.url).then((res) => {
        if (!res.ok) throw new ApiError(res.status, 'Failed to download PDF');
        return res.blob();
      }),
  );
}

export interface Review {
  reviewerName: string;
  score: number;
  reviewText?: string;
  photoUrls: string[];
  createdAt: string;
}

export interface GetRatingsResponse {
  packageId: string;
  ratings: Review[];
  count: number;
  nextCursor: string | null;
  averageRating: number;
  totalRatings: number;
}

export function getRatings(
  packageId: string,
  params?: { limit?: number; cursor?: string },
): Promise<GetRatingsResponse> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.cursor) qs.set('cursor', params.cursor);
  const query = qs.toString();
  return request<GetRatingsResponse>(
    `/packages/${packageId}/ratings${query ? `?${query}` : ''}`,
  );
}

export function submitRating(
  packageId: string,
  body: {
    reviewerName: string;
    reviewerEmail: string;
    score: number;
    reviewText?: string;
    photoUrls?: string[];
  },
): Promise<unknown> {
  return request(`/packages/${packageId}/ratings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function uploadReviewPhoto(
  packageId: string,
  file: File,
): Promise<{ url: string }> {
  const base64Data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

  return request<{ url: string }>(`/packages/${packageId}/ratings/upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type,
      data: base64Data,
    }),
  });
}

export function recordAnalyticsEvent(body: {
  eventType: string;
  packageId?: string;
  date?: string;
}): Promise<unknown> {
  return request('/analytics/event', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Record a unique visit — fires at most once per day per visitor.
 * Uses localStorage to track visitor ID and last visit date.
 */
export function recordUniqueVisit(): void {
  try {
    const today = new Date().toISOString().slice(0, 10);
    const lastVisitDate = localStorage.getItem('fam_last_visit');
    if (lastVisitDate === today) return; // already recorded today

    // Generate a visitor ID if we don't have one
    let visitorId = localStorage.getItem('fam_visitor_id');
    if (!visitorId) {
      visitorId = crypto.randomUUID();
      localStorage.setItem('fam_visitor_id', visitorId);
    }

    localStorage.setItem('fam_last_visit', today);
    recordAnalyticsEvent({ eventType: 'visit', date: today }).catch(() => {});
  } catch {
    // localStorage unavailable (private browsing etc.) — skip silently
  }
}

// ============================================================
// Admin API — Auth
// ============================================================

export function adminLogin(body: {
  email: string;
  password: string;
}): Promise<unknown> {
  return request('/admin/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function adminRefresh(): Promise<unknown> {
  return adminRequest('/admin/auth/refresh', { method: 'POST' });
}

export function adminLogout(): Promise<unknown> {
  return adminRequest('/admin/auth/logout', { method: 'POST' });
}

// ============================================================
// Admin API — CRUD helpers
// ============================================================

function crudEndpoints(resource: string) {
  const base = `/admin/${resource}`;
  return {
    list: () => adminRequest<unknown>(base),
    create: (body: unknown) => adminJson<unknown>(base, 'POST', body),
    update: (id: string, body: unknown) =>
      adminJson<unknown>(`${base}/${id}`, 'PUT', body),
    remove: (id: string) =>
      adminRequest<unknown>(`${base}/${id}`, { method: 'DELETE' }),
  };
}

export const adminPackages = crudEndpoints('packages');
export const adminRecipes = crudEndpoints('recipes');
export const adminThemes = crudEndpoints('themes');
export const adminWinePairings = crudEndpoints('wine-pairings');
export const adminEntertainmentStarters = crudEndpoints('entertainment-starters');
export const adminTablescaping = crudEndpoints('tablescaping');

// ============================================================
// Admin API — Image Upload
// ============================================================

export async function adminUploadImage(file: File): Promise<{ url: string }> {
  const base64Data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data URL prefix (e.g. "data:image/jpeg;base64,")
      resolve(result.split(',')[1]);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

  return adminJson<{ url: string }>('/admin/upload', 'POST', {
    fileName: file.name,
    contentType: file.type,
    data: base64Data,
  });
}

// ============================================================
// Admin API — Audit log
// ============================================================

export function getAuditLog(): Promise<unknown> {
  return adminRequest('/admin/audit-log');
}

// ============================================================
// Admin API — Analytics
// ============================================================

function analyticsQuery(
  endpoint: string,
  params?: { startDate?: string; endDate?: string },
): Promise<unknown> {
  const qs = new URLSearchParams();
  if (params?.startDate) qs.set('startDate', params.startDate);
  if (params?.endDate) qs.set('endDate', params.endDate);
  const query = qs.toString();
  return adminRequest(`/admin/analytics/${endpoint}${query ? `?${query}` : ''}`);
}

export function getAnalyticsVisits(params?: {
  startDate?: string;
  endDate?: string;
}): Promise<unknown> {
  return analyticsQuery('visits', params);
}

export function getAnalyticsDownloads(params?: {
  startDate?: string;
  endDate?: string;
}): Promise<unknown> {
  return analyticsQuery('downloads', params);
}

export function getAnalyticsPageViews(params?: {
  startDate?: string;
  endDate?: string;
}): Promise<unknown> {
  return analyticsQuery('page-views', params);
}

export function getAnalyticsTopPackages(params?: {
  startDate?: string;
  endDate?: string;
}): Promise<unknown> {
  return analyticsQuery('top-packages', params);
}
